<script setup lang="ts">
import { computed, watch } from 'vue';
import {
  CdrContainer, CdrText, CdrLink,
} from '@rei/cedar';

import { useAnalytics } from '../../../common/composables/useAnalytics';
import { useTags } from '../../../common/composables/useTags';
import useAdobeExperiment from '../../../common/composables/useAdobeExperiment';

const props = defineProps<{ userInfo: Record<string, string|boolean> }>();
const { adobeExperimentValue: adobeExperimentMembershipInfoValue } = useAdobeExperiment({
  adobeTargetLocation: 'help-membership-info-help-homepage',
});

watch(
  () => [props.userInfo.isMember, adobeExperimentMembershipInfoValue.value],
  ([isMember, experimentValue]) => {
    if (!isMember || !experimentValue) return; // Stop execution if there isn't member or experiment valid

    const tagControl = useTags('getTag', 'HELP_CENTER_MEMBER_INFO_CONTROL');
    const tagVariant = useTags('getTag', 'HELP_CENTER_MEMBER_INFO_VARIANT');

    const pageName = experimentValue === 'A'
      ? tagControl
      : tagVariant;

    useAnalytics('sendViewAnalytics', { pageName });
  },
);

const dividendBalance = computed(() => {
  if (props?.userInfo.dividendBalance !== null) {
    const balance = parseFloat(String(props?.userInfo.dividendBalance));
    const formattedBalance = balance.toFixed(2).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');

    return `$${formattedBalance}`;
  }

  return null;
});

const memberJoinYear = computed(() => {
  if (props?.userInfo.memberJoinDate) {
    return new Date(String(props?.userInfo.memberJoinDate)).getFullYear();
  }

  return null;
});

const handleRewardsActivityClick = () => {
  useAnalytics('sendClickAnalytics', {
    pageName: null,
    linkName: useTags('getTag', 'HELP_CENTER_HOMEPAGE_YOUR_REWARDS_ACTIVITY_CLICK'),
  });
};
</script>

<template>
  <CdrContainer
    v-if="userInfo?.isLoggedIn && userInfo?.isMember && adobeExperimentMembershipInfoValue === 'B'"
    tag="section"
    class="wrapper"
  >
    <CdrContainer class="primary-membership-info">
      <CdrText
        class="customer-full-name"
      >
        {{ userInfo.firstName }} {{ userInfo.lastName }}
      </CdrText>

      <CdrText class="membership-date">
        MEMBER SINCE {{ memberJoinYear }}
      </CdrText>
    </CdrContainer>

    <CdrContainer class="secondary-membership-info">
      <CdrText>Member number: #{{ userInfo.memberNumber }}</CdrText>

      <CdrText class="reward-balance">
        Total REI Rewards: <span class="reward-amount">{{ dividendBalance }}</span>
      </CdrText>

      <CdrLink
        class="rewards-activity-button"
        href="/account/member-rewards"
        @click="handleRewardsActivityClick"
      >
        Your rewards activity
      </CdrLink>
    </CdrContainer>
  </CdrContainer>
</template>

<style scoped lang="scss">
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';

.wrapper {
  max-width: 1170px;
  margin-left: auto;
  margin-right: auto;
  padding: $cdr-space-two-x 0 $cdr-space-two-x $cdr-space-one-x;
}

.primary-membership-info {
  display: flex;
  flex-direction: column;
  margin-bottom: 1.5rem;
  width: fit-content;

  .customer-full-name {
    @include cdr-text-heading-serif-600;
  }

  .membership-date {
    @include cdr-text-eyebrow-100;
  }
}

.secondary-membership-info {
  width: auto;
  display: flex;
  flex-direction: column;
  width: fit-content;

  .reward-balance {
    font-weight: $cdr-text-strong-weight;
  }

  .reward-amount {
    color: $cdr-color-background-button-primary-active;
  }
}

.rewards-activity-button {
  @include cdr-text-utility-sans-200;
  padding-top: $cdr-space-half-x;
  text-decoration: none!important;
}
</style>
